import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GuestLayout from '../components/GuestLayout';
import axios from 'axios';
import { Wifi, Trash2, Gamepad2, Tv, Lock, Thermometer, ShieldAlert, MapPin, LogOut as LogOutIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Wifi, Trash2, Gamepad2, Tv, Lock, Thermometer, ShieldAlert, MapPin, LogOut: LogOutIcon
};

export default function HomePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [sections, setSections] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/property`),
      axios.get(`${API}/sections`)
    ]).then(([propRes, secRes]) => {
      setProperty(propRes.data);
      setSections(secRes.data);
    }).catch(console.error);
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <GuestLayout>
      <div className="px-6 pt-8 max-w-md mx-auto md:max-w-2xl">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-text-muted text-sm font-medium mb-1">{greeting()}</p>
              <h1 data-testid="home-greeting" className="font-heading text-3xl md:text-4xl tracking-tight text-espresso">
                {user?.name || 'Welcome'}
              </h1>
            </div>
            <button
              data-testid="guest-logout-btn"
              onClick={() => { logout(); navigate('/login'); }}
              className="mt-1 text-text-muted/50 hover:text-espresso transition-colors p-2"
              title="Sign Out"
            >
              <LogOutIcon size={20} />
            </button>
          </div>
          {property && (
            <p className="text-text-muted text-sm mt-2">{property.name}</p>
          )}
          {user?.check_in && user?.check_out && (
            <p className="text-text-muted text-xs mt-1">
              {new Date(user.check_in + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} &ndash; {new Date(user.check_out + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>

        {/* Quick Info Card */}
        {property?.wifi_network && (
          <div data-testid="wifi-card" className="bg-white border border-warm-border rounded-2xl p-5 mb-8 animate-fade-in shadow-[0_2px_12px_-4px_rgba(44,42,38,0.08)]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-sand flex items-center justify-center">
                <Wifi size={18} className="text-espresso" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-text-muted font-semibold">WiFi</p>
                <p className="text-espresso font-semibold">{property.wifi_network}</p>
              </div>
            </div>
            <div className="bg-sand rounded-xl px-4 py-3">
              <p className="text-xs text-text-muted mb-1">Password</p>
              <p data-testid="wifi-password" className="text-espresso font-mono text-lg font-semibold tracking-wide">{property.wifi_password}</p>
            </div>
          </div>
        )}

        {/* Property Hero */}
        {property?.image_url && (
          <div className="relative rounded-2xl overflow-hidden mb-8 animate-fade-in aspect-[16/9]">
            <img
              src={property.image_url}
              alt={property.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-espresso/60 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-5 right-5">
              <h2 className="font-heading text-white text-xl mb-1">{property.name}</h2>
              <p className="text-white/80 text-sm">{property.address}</p>
            </div>
          </div>
        )}

        {/* Property Guide Sections */}
        <div className="mb-6">
          <h2 className="font-heading text-xl text-espresso mb-4">Property Guide</h2>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-8">
          {sections.map((section, idx) => {
            const IconComp = iconMap[section.icon] || MapPin;
            return (
              <button
                key={section.id}
                data-testid={`section-card-${idx}`}
                onClick={() => navigate(`/sections/${section.id}`)}
                className="bg-white border border-warm-border p-5 rounded-2xl text-left hover:border-warmgold/50 transition-colors cursor-pointer card-hover opacity-0 animate-fade-in"
                style={{ animationDelay: `${idx * 0.05}s` }}
              >
                <div className="w-10 h-10 rounded-xl bg-sand flex items-center justify-center mb-3">
                  <IconComp size={18} className="text-espresso" />
                </div>
                <h3 className="font-body text-sm font-semibold text-espresso leading-tight mb-1">{section.title}</h3>
                <p className="text-text-muted text-xs leading-relaxed line-clamp-2">{section.description}</p>
              </button>
            );
          })}
        </div>
      </div>
    </GuestLayout>
  );
}
